import React, {useState} from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Paper,
    TextField,
    Typography,
} from "@mui/material";

import {executeAdminCode} from "../api/api";

import {CodeExecutionResponse} from "../types/coderunner";
import CodeTextarea from "../components/CodeTextarea";

const AdminCodeRunnerPage: React.FC = () => {
    const [language, setLanguage] = useState("java");
    const [code, setCode] = useState<string>(
        `import java.util.Scanner;
public class Main {
    public static void main(String[] args) {
        Scanner sc = new Scanner(System.in);
        int a = sc.nextInt();
        int b = sc.nextInt();
        System.out.println(a + b);
    }
}`
    );

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [response, setResponse] = useState<CodeExecutionResponse | null>(null);

    const handleRunCode = async () => {
        setLoading(true);
        setError(null);
        setResponse(null);

        try {
            const res = await executeAdminCode(code);
            setResponse(res);
        } catch (e: any) {
            console.error(e);
            setError(
                e?.response?.data?.message ||
                e?.message ||
                "Failed to execute code. Check backend logs."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="lg" sx={{mb: 4}}>
            <Typography variant="h5" gutterBottom>
                Admin Code Runner
            </Typography>

            <Paper
                elevation={1}
                sx={(theme) => ({
                    p: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    mb: 2,
                })}
            >
                <Typography variant="subtitle1" gutterBottom>
                    Your solution
                </Typography>
                <Box sx={(theme) => ({
                    p: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    mb: 2,
                })}>
                    <CodeTextarea
                        height={400}
                        language={language}
                        value={code}
                        onChange={setCode}
                    />
                </Box>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                >
                    <TextField
                        select
                        label="Language"
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        size="small"
                        sx={{width: 200}}
                    >
                        <MenuItem value="java">Java</MenuItem>
                        <MenuItem value="python">Python</MenuItem>
                        <MenuItem value="go">Go</MenuItem>
                    </TextField>

                    <Box display="flex" alignItems="center" gap={2}>
                        {loading && <CircularProgress size={24}/>}
                        <Button
                            variant="contained"
                            onClick={handleRunCode}
                            disabled={loading || !code.trim()}
                        >
                            Run Code
                        </Button>
                    </Box>
                </Box>

                {error && (
                    <Box mt={2}>
                        <Alert severity="error">{error}</Alert>
                    </Box>
                )}
            </Paper>

            {response && (
                <Paper
                    elevation={1}
                    sx={(theme) => ({
                        p: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        mb: 2,
                    })}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Execution Result</Typography>

                        <Box display="flex" gap={1}>
                            <Chip
                                label={response.compiledSuccessfully ? "Compiled" : "Compilation Error"}
                                color={response.compiledSuccessfully ? "success" : "error"}
                                size="small"
                            />
                            <Chip
                                label={response.allPassed ? "All Tests Passed" : "Some Tests Failed"}
                                color={response.allPassed ? "success" : "warning"}
                                size="small"
                            />
                        </Box>
                    </Box>

                    {/* Compilation output */}
                    {(response.compileStdout || response.compileStderr) && (
                        <Box mt={2}>
                            <Typography variant="subtitle2">Compilation Output</Typography>
                            {response.compileStdout && (
                                <Box mt={1}>
                                    <Typography variant="body2" fontWeight="bold">
                                        stdout:
                                    </Typography>
                                    <Paper variant="outlined" sx={{p: 1, mt: 0.5, whiteSpace: "pre-wrap"}}>
                                        {response.compileStdout}
                                    </Paper>
                                </Box>
                            )}
                            {response.compileStderr && (
                                <Box mt={1}>
                                    <Typography variant="body2" fontWeight="bold">
                                        stderr:
                                    </Typography>
                                    <Paper variant="outlined" sx={{p: 1, mt: 0.5, whiteSpace: "pre-wrap"}}>
                                        {response.compileStderr}
                                    </Paper>
                                </Box>
                            )}
                        </Box>
                    )}

                    <Divider sx={{my: 2}}/>

                    <Typography variant="subtitle2" gutterBottom>
                        Test Cases
                    </Typography>

                    {response.results.length === 0 && (
                        <Typography variant="body2">No test results returned.</Typography>
                    )}

                    {response.results.length > 0 && (
                        <List dense>
                            {response.results.map((r, index) => (
                                <React.Fragment key={index}>
                                    <ListItem
                                        alignItems="flex-start"
                                        sx={{
                                            borderBottom: "1px solid #eee",
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Typography variant="body1">
                                                        Test #{index + 1}
                                                    </Typography>
                                                    <Chip
                                                        label={r.passed ? "PASSED" : "FAILED"}
                                                        color={r.passed ? "success" : "error"}
                                                        size="small"
                                                    />
                                                </Box>
                                            }
                                            secondary={
                                                <Box mt={1}>
                                                    <Typography variant="body2">
                                                        <strong>Input:</strong> {r.input}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>Expected:</strong> {r.expectedOutput}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>Actual:</strong>{" "}
                                                        {r.actualOutput !== null ? r.actualOutput : "<null>"}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{mt: 0.5}}>
                                                        <strong>Message:</strong> {r.message}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </Paper>
            )}
        </Container>
    );
};

export default AdminCodeRunnerPage;